/* eslint-disable no-underscore-dangle */
/* eslint-disable no-unused-expressions */
/* eslint-disable no-inner-declarations */
import { uniq, omit } from 'lodash';
import { push } from 'react-router-redux';
import moment from 'moment';
import { notification } from 'antd';
import uuidv4 from 'uuid/v4';
import { put, takeLatest, select, call } from 'redux-saga/effects';
import { delay } from 'redux-saga';
import { setNotification, getTranslated, locales, operatorTyping, searchByOrOperator, searchElementMatch, ADMIN_CUSTOMER_ID } from 'utils';
import * as DesignLibraryActions from '../actions/designLibraryActions';
import * as DesignLibraryApi from '../api/designLibraryApi';
import { getUsersApi } from '../api/userApi';
import { getPropertyValuesApi } from '../api/propertyApi';
import { getCustomersApi } from '../api/customerApi';
import * as DesignLibraryTypes from '../types/designLibraryTypes';

const getLayout = state => state.layout;
const getDesignLibrary = state => state.designLibrary;

// L O A D   D E S I G N   L I B R A R Y   L I S T
function* loadDesignLibraryListSaga() {
    try {
        const { locale } = yield select(getLayout);
        const designLibrary = yield select(getDesignLibrary);

        const { offset, limit, filters, sort } = designLibrary.list;

        const params = {
            offset,
            limit,
        };

        if (sort !== null) {
            const sortInt = sort.type === 'asc' ? 1 : -1;
            if (sort.key === 'created_at') {
                params.sort_by = JSON.stringify({ created_at: sortInt });
            }
        }

        const filterParams = {};

        if (filters.librarySearch !== '') {
            filterParams.or = searchByOrOperator(['search', 'comment', 'tags'], filters.librarySearch.trim());
            filterParams.or = [
                ...filterParams.or,
                { 'name.translate': searchElementMatch(filters.librarySearch.trim(), locale) },
            ];
        }

        if (filters.prefix !== '') {
            filterParams['design_variants.prefix'] = operatorTyping(filters.prefix.trim());
        }

        if (filters.category !== undefined) {
            filterParams.category_ids = filters.category;
        }

        if (filters.theme !== undefined) {
            filterParams.theme_ids = filters.theme;
        }

        if (filters.subTheme !== undefined) {
            filterParams.sub_theme_ids = filters.subTheme;
        }

        if (filters.state !== undefined) {
            filterParams['design_variants.state'] = filters.state;
        }

        if (filters.is_exclusive !== undefined) {
            filterParams.is_exclusive = filters.is_exclusive === 'yes';
        }

        const variantElemMatch = {};

        if (filters.size !== '') {
            const dimensions = { firstValue: null, secondValue: null };

            const splitValues = filters.size.toLowerCase().split('x');

            if (splitValues?.length === 1) {
                dimensions.firstValue = Math.round(parseFloat(splitValues[0].trim()) * 10);

                variantElemMatch.or = [
                    { commercial_length: dimensions.firstValue },
                    { commercial_width: dimensions.firstValue },
                ];
            } else if (splitValues?.length === 2) {
                dimensions.firstValue = Math.round(parseFloat(splitValues[0].trim()) * 10);
                dimensions.secondValue = Math.round(parseFloat(splitValues[1].trim()) * 10);

                variantElemMatch.or = [
                    {
                        commercial_length: dimensions.firstValue,
                        commercial_width: dimensions.secondValue,
                    },
                    {
                        commercial_length: dimensions.secondValue,
                        commercial_width: dimensions.firstValue,
                    },
                ];
            } else {
                variantElemMatch.commercial_length = null;
                variantElemMatch.commercial_width = null;
            }
        }

        if (filters.ratio !== '') {
            filterParams['design_variants.ratio'] = parseFloat(filters.ratio.trim());
        }

        if (filters.border !== undefined) {
            filterParams['design_variants.border'] = filters.border === 'none' ? null : parseFloat(filters.border);
        }

        if (filters.quality !== undefined) {
            variantElemMatch.qualities = filters.quality;
        }
        if (filters.state !== undefined) {
            variantElemMatch.state = filters.state;
        }
        if (filters.border !== undefined) {
            variantElemMatch.border = filters.border === 'none' ? null : parseFloat(filters.border);
        }
        if (Object.keys(variantElemMatch).length > 0 && !filterParams.or) {
            delete filterParams['design_variants.qualities'];
            delete filterParams['design_variants.state'];
            delete filterParams['design_variants.border'];
            delete filterParams['design_variants.commercial_length'];
            delete filterParams['design_variants.commercial_width'];
            filterParams.design_variants = { elementMatch: variantElemMatch };
        }

        if (filters.customerId !== '') {
            const exclusivityOr = [
                { 'exclusivities.customer_id': filters.customerId },
                { exclusivities: null },
            ];
            filterParams.or = filterParams.or ? [...filterParams.or, ...exclusivityOr] : exclusivityOr;
        }

        let result = null;
        if (Object.keys(variantElemMatch).length > 0) {
            const variantFilter = { ...variantElemMatch };
            if (filterParams['design_variants.ratio']) {
                variantFilter.ratio = filterParams['design_variants.ratio'];
                delete filterParams['design_variants.ratio'];
            }
            if (filterParams['design_variants.prefix']) {
                variantFilter.prefix = filterParams['design_variants.prefix'];
                delete filterParams['design_variants.prefix'];
            }

            const variantParams = {
                offset: params.offset,
                limit: params.limit,
            };

            if (filterParams.or) {
                const joinOr = filterParams.or.map(cond => {
                    const newCond = {};
                    Object.keys(cond).forEach((k) => {
                        newCond[`library.${k}`] = cond[k];
                    });
                    return newCond;
                });

                variantFilter.join = { or: joinOr };

                variantParams.joins = JSON.stringify(['library']);
            }

            const libraryLevelKeys = ['category_ids', 'theme_ids', 'sub_theme_ids', 'is_exclusive'];
            let addedLibraryJoin = !!variantParams.joins;
            libraryLevelKeys.forEach((key) => {
                if (Object.prototype.hasOwnProperty.call(filterParams, key) && filterParams[key] !== undefined && filterParams[key] !== null && filterParams[key] !== '') {
                    variantFilter[`library.${key}`] = filterParams[key];
                    addedLibraryJoin = true;
                }
            });
            if (addedLibraryJoin) {
                variantParams.joins = JSON.stringify(['library']);
            }
            variantParams.filter = JSON.stringify(variantFilter);

            result = yield DesignLibraryApi.getDesignLibraryVariantsApi(variantParams);

            if (result.status === 200) {
                const data = {
                    total: result.data.total,
                    hits: result.data.hits.map(hit => ({
                        ...hit,
                        _id: hit.design_variant_id || hit.id || hit._id,
                        library: hit.library ? [omit(hit.library, ['design_variants'])] : (hit.library_id ? [{ _id: hit.library_id }] : []),
                    })),
                };

                yield put(DesignLibraryActions.loadDesignLibraryListRes(data));
            } else {
                yield put(DesignLibraryActions.loadDesignLibraryListErr());
            }
        } else {
            params.filter = JSON.stringify(filterParams);

            result = yield DesignLibraryApi.getDesignLibraryApi(params);
            if (result.status === 200) {
                const data = {
                    total: result.data.total,
                    hits: result.data.hits.map(hit => (
                        hit.design_variants.map(variant => ({
                            ...variant,
                            _id: variant.design_variant_id,
                            library: [omit(hit, ['design_variants'])],
                        })))).flat(2),
                };

                if (Object.keys(variantElemMatch).length > 0) {
                    const matchesVariant = (variant) => {
                        if (variantElemMatch.or && Array.isArray(variantElemMatch.or)) {
                            const ok = variantElemMatch.or.some(cond => Object.keys(cond).every(k => variant[k] === cond[k]));
                            if (!ok) return false;
                        }

                        if (variantElemMatch.qualities && variantElemMatch.qualities !== variant.qualities) return false;
                        if (variantElemMatch.state && variantElemMatch.state !== variant.state) return false;
                        if (Object.prototype.hasOwnProperty.call(variantElemMatch, 'border')) {
                            const borderCond = variantElemMatch.border;
                            if (borderCond === null) {
                                if (variant.border !== null) return false;
                            } else if (variant.border !== borderCond) return false;
                        }

                        return true;
                    };

                    data.hits = data.hits.filter(matchesVariant);
                    data.total = data.hits.length;
                }

                yield put(DesignLibraryActions.loadDesignLibraryListRes(data));
            } else {
                yield put(DesignLibraryActions.loadDesignLibraryListErr());
            }
        }
    } catch (e) {
        yield put(DesignLibraryActions.loadDesignLibraryListErr());
        console.log(e);
    }
}
export function* loadDesignLibraryListWatcher() {
    yield takeLatest(DesignLibraryTypes.LOAD_DESIGN_LIBRARY_LIST, loadDesignLibraryListSaga);
}
// L O A D   D E S I G N   L I B R A R Y   L I S T

// T O G G L E   D E S I G N   L I B R A R Y   I T E M   S T A T E
function* toggleDesignLibraryItemStateSaga(action) {
    const layout = yield select(getLayout);
    const { locale } = layout;

    try {
        const result = yield DesignLibraryApi.toggleDesignLibraryItemStateApi(action.data);
        if (result.status === 202) {
            yield call(loadDesignLibraryListSaga);
            setNotification('success', locale, 'global.success', 'list.notification.toggleState.success');
        } else {
            setNotification('error', locale, 'global.error', 'list.notification.toggleState.error');
        }
    } catch (e) {
        setNotification('error', locale, 'global.error', 'list.notification.toggleState.error');
        console.log(e);
    }
}

export function* toggleDesignLibraryItemStateWatcher() {
    yield takeLatest(DesignLibraryTypes.TOGGLE_DESIGN_LIBRARY_ITEM_STATE, toggleDesignLibraryItemStateSaga);
}
// T O G G L E   D E S I G N   L I B R A R Y   I T E M   S T A T E

// L O A D   D E S I G N   L I B R A R Y   S E T T I N G S
function* loadDesignLibrarySettingsSaga() {
    try {
        const designLibrary = yield select(getDesignLibrary);

        const { offset, limit } = designLibrary.settings;

        const params = {
            offset,
            limit,
        };

        const result = yield DesignLibraryApi.getDesignLibrarySettingsApi(params);

        if (result.status === 200) {
            yield put(DesignLibraryActions.loadDesignLibrarySettingsRes(result.data));
        } else {
            yield put(DesignLibraryActions.loadDesignLibrarySettingsErr());
        }
    } catch (e) {
        yield put(DesignLibraryActions.loadDesignLibrarySettingsErr());
        console.log(e);
    }
}
export function* loadDesignLibrarySettingsWatcher() {
    yield takeLatest(DesignLibraryTypes.LOAD_DESIGN_LIBRARY_SETTINGS, loadDesignLibrarySettingsSaga);
}
// L O A D   D E S I G N   L I B R A R Y   S E T T I N G S

// L O A D   V I E W   D E S I G N   L I B R A R Y
function* loadViewDesignLibrarySaga(action) {
    const { admin } = yield select(getLayout);

    try {
        const params = {
            offset: 0,
            limit: 1,
        };

        const filterParams = { _id: action.id };

        params.filter = JSON.stringify(filterParams);

        const result = yield DesignLibraryApi.getDesignLibraryApi(params);

        if (result.data.total === 1) {
            const [designLibrary] = result.data.hits;
            let catThemeIDS = [...(designLibrary.category_ids || []), ...(designLibrary.theme_ids || []), ...(designLibrary.sub_theme_ids || [])];
            catThemeIDS = uniq(catThemeIDS);
            const resultSetting = yield DesignLibraryApi.loadDesignLibrarySettingsApi({ filter: JSON.stringify({ _id: catThemeIDS }) });
            const userIDs = [designLibrary.created_user];
            let resultCustomer = null;

            designLibrary.design_variants.forEach((variant) => {
                variant.updated_by && userIDs.push(variant.updated_by);
                variant.created_user && userIDs.push(variant.created_user);
            });

            const resultUsers = yield getUsersApi({ filter: JSON.stringify({ _id: userIDs }) });

            if ((designLibrary.exclusivities || []).length > 0) {
                resultCustomer = yield getCustomersApi({ filter: JSON.stringify({ _id: designLibrary.exclusivities[0].customer_id }), joins: JSON.stringify(['main_contact']) });
            }

            if (resultUsers.status === 200) {
                if (resultUsers.data.total > 0) {
                    const designCreatedByUser = resultUsers.data.hits.find(user => user._id === designLibrary.created_user);
                    designLibrary.created_user_name = designCreatedByUser ? `${designCreatedByUser?.first_name} ${designCreatedByUser?.last_name}` : null;

                    designLibrary.design_variants = designLibrary.design_variants.map((variant) => {
                        const variantUpdatedByUser = resultUsers.data.hits.find(user => user._id === variant.updated_by);
                        const variantCreatedByUser = resultUsers.data.hits.find(user => user._id === variant.created_user);

                        return {
                            ...variant,
                            updated_by_user: variantUpdatedByUser ? `${variantUpdatedByUser.first_name} ${variantUpdatedByUser.last_name}` : null,
                            created_by_user: variantCreatedByUser ? `${variantCreatedByUser.first_name} ${variantCreatedByUser.last_name}` : null,
                        };
                    });
                } else {
                    designLibrary.created_user_name = null;
                }

                if (resultCustomer) {
                    if (resultCustomer.data.total !== 0) {
                        const [customer] = resultCustomer.data.hits;
                        designLibrary.exclusivities_customer_name = `${customer[ADMIN_CUSTOMER_ID[admin]]}${customer?.company ? ` ~ ${customer?.company}` : ''}`;
                    } else {
                        designLibrary.exclusivities_customer_name = null;
                    }
                } else {
                    designLibrary.exclusivities_customer_name = null;
                }

                if (resultSetting.data) {
                    if (resultSetting.data.total !== 0) {
                        designLibrary.settings = resultSetting.data.hits;
                    } else {
                        designLibrary.settings = null;
                    }
                } else {
                    designLibrary.settings = null;
                }

                yield put(DesignLibraryActions.loadViewDesignLibraryRes({
                    ...result.data,
                    hits: result.data.hits.map(hit => ({
                        ...hit,
                        design_variants: hit.design_variants.map(dv => ({
                            ...dv,
                            print_version: `${dv.print_version}?v=${moment().unix()}`,
                        })),
                    })),
                }));
            }
        } else {
            yield put(DesignLibraryActions.loadViewDesignLibraryErr());
        }
    } catch (e) {
        yield put(DesignLibraryActions.loadViewDesignLibraryErr());
        console.log(e);
    }
}
export function* loadViewDesignLibraryWatcher() {
    yield takeLatest(DesignLibraryTypes.LOAD_VIEW_DESIGN_LIBRARY, loadViewDesignLibrarySaga);
}
// L O A D   V I E W   D E S I G N   L I B R A R Y

// A D D   D E S I G N   L I B R A R Y   S E T T I N G S
function* addDesignLibrarySettingsSaga(action) {
    const layout = yield select(getLayout);
    const { locale, userData } = layout;
    try {
        // eslint-disable-next-line prefer-destructuring
        let data = action.data;

        data = {
            ...data,
            parent_id: action.data.parent_id && action.data.type === 'SUB_THEME' ? action.data.parent_id : null,
            id: uuidv4(),
            created_user: userData.data._id,
        };

        const result = yield DesignLibraryApi.addDesignLibrarySettingsApi(data);

        if (result.status === 202) {
            yield call(loadDesignLibrarySettingsSaga);
            setNotification('success', locale, 'global.success', `settings.notification.add.${action.data.type}.success`);
        } else {
            setNotification('error', locale, 'global.error', `settings.notification.add.${action.data.type}.error`);
        }
    } catch (e) {
        setNotification('error', locale, 'global.error', `settings.notification.add.${action.data.type}.error`);
        console.log(e);
    }
}
export function* addDesignLibrarySettingsWatcher() {
    yield takeLatest(DesignLibraryTypes.ADD_DESIGN_LIBRARY_SETTINGS, addDesignLibrarySettingsSaga);
}
// A D D   D E S I G N   L I B R A R Y   S E T T I N G S

// E D I T   D E S I G N   L I B R A R Y   S E T T I N G S
function* editDesignLibrarySettingsSaga(action) {
    const layout = yield select(getLayout);
    const { locale, userData } = layout;
    try {
        const data = {
            id: action.data.id,
            value: action.data.value,
            parent_id: action.data.parent_id && action.data.type === 'SUB_THEME' ? action.data.parent_id : null,
            created_user: userData.data._id,
        };
        const result = yield DesignLibraryApi.editDesignLibrarySettingsApi(data);

        if (result.status === 202) {
            yield call(loadDesignLibrarySettingsSaga);
            setNotification('success', locale, 'global.success', `settings.notification.edit.${action.data.type}.success`);
        } else {
            setNotification('error', locale, 'global.error', `settings.notification.edit.${action.data.type}.error`);
        }
    } catch (e) {
        setNotification('error', locale, 'global.error', `settings.notification.edit.${action.data.type}.error`);
        console.log(e);
    }
}
export function* editDesignLibrarySettingsWatcher() {
    yield takeLatest(DesignLibraryTypes.EDIT_DESIGN_LIBRARY_SETTINGS, editDesignLibrarySettingsSaga);
}
// E D I T   D E S I G N   L I B R A R Y   S E T T I N G S

// D E L E T E   D E S I G N   L I B R A R Y   S E T T I N G S
function* deleteDesignLibrarySettingsSaga(action) {
    const layout = yield select(getLayout);
    const { locale } = layout;
    try {
        const result = yield DesignLibraryApi.deleteDesignLibrarySettingsApi({ id: action.data.id });

        if (result.status === 202) {
            yield call(loadDesignLibrarySettingsSaga);
            setNotification('success', locale, 'global.success', `settings.notification.delete.${action.data.type}.success`);
        } else {
            yield put(DesignLibraryActions.deleteDesignLibrarySettingsErr());
            setNotification('error', locale, 'global.error', `settings.notification.delete.${action.data.type}.error`);
        }
    } catch (e) {
        yield put(DesignLibraryActions.deleteDesignLibrarySettingsErr());
        setNotification('error', locale, 'global.error', `settings.notification.delete.${action.data.type}.error`);
        console.log(e);
    }
}
export function* deleteDesignLibrarySettingsWatcher() {
    yield takeLatest(DesignLibraryTypes.DELETE_DESIGN_LIBRARY_SETTINGS, deleteDesignLibrarySettingsSaga);
}
// D E L E T E   D E S I G N   L I B R A R Y   S E T T I N G S

// L O A D   L I B R A R Y   L I S T
function* loadLibrarySaga(action) {
    const layout = yield select(getLayout);
    const { locale } = layout;

    try {
        const designLibrary = yield select(getDesignLibrary);
        const { offset, limit } = designLibrary.listDesignVariant;

        const params = {
            filter: JSON.stringify({ library_id: action.libraryID }),
            offset,
            limit,
        };

        const result = yield DesignLibraryApi.getDesignLibraryVariantsApi(params);

        yield put(DesignLibraryActions.loadLibraryRes({
            ...result.data,
            hits: result.data.hits.map(hit => ({
                ...hit,
                thumbnail: `${hit.thumbnail}?v=${moment().unix()}`,
            })),
        }));
    } catch (e) {
        setNotification('error', locale, 'global.error', 'designLibrary.notification.loadPrintVersionError');
        yield put(DesignLibraryActions.loadLibraryErr());
        console.log(e);
    }
}

export function* loadLibraryWatcher() {
    yield takeLatest(DesignLibraryTypes.LOAD_LIBRARY_LIST, loadLibrarySaga);
}
// L O A D   L I B R A R Y   L I S T

// L O A D   E D I T A B L E   D E S I G N   L I B R A R Y  F O R   D E S I G N
function* loadEditableDesignLibraryForDesignSaga(action) {
    const layout = yield select(getLayout);
    const { locale } = layout;

    try {
        const designLibrary = yield select(getDesignLibrary);
        const { offset, limit } = designLibrary.listDesignVariant;

        const params = {
            filter: JSON.stringify({ _id: action.id }),
            offset,
            limit,
        };

        const result = yield DesignLibraryApi.getDesignLibraryVariantsApi(params);

        if (result.data.hits.length === 0) {
            notification.error({
                message: 'Error',
                description: 'Print version don\'t exist',
            });

            yield put(push('/404'));
            yield put(DesignLibraryActions.addDesignVariantErr());
        } else {
            const [variant] = result.data.hits;
            yield put(DesignLibraryActions.loadEditableDesignLibraryForDesignRes(variant));
        }
    } catch (e) {
        setNotification('error', locale, 'global.error', 'designLibrary.notification.loadPrintVersionError');
        yield put(DesignLibraryActions.loadEditableDesignLibraryForDesignErr());
        console.log(e);
    }
}

export function* loadEditableDesignLibraryForDesignWatcher() {
    yield takeLatest(DesignLibraryTypes.LOAD_EDITABLE_DESIGN_LIBRARY_FOR_DESIGN, loadEditableDesignLibraryForDesignSaga);
}
// L O A D   E D I T A B L E   D E S I G N   L I B R A R Y  F O R   D E S I G N


// A D D   N E W   D E S I G N   V A R I A N T
function* addDesignVariantSaga(action) {
    const layout = yield select(getLayout);
    const { locale } = layout;
    const finalDescription = 'designLibrary.notification.newPrintVersionAddedSuccess';

    try {
        const formData = new FormData();

        const dataKeys = Object.keys(action.data);
        dataKeys.forEach((key) => {
            if (key !== 'file' && key !== 'qualities' && key !== 'colors') {
                formData.append(key, action.data[key]);
            }
        });

        if (action.data.qualities) {
            formData.append('qualities', JSON.stringify(action.data.qualities));
        }

        if (action.data.file && action.data.file[0] === null) {
            formData.append('file', '');
        } else if ((action.data.file) && (action.data.file[0].url)) {
            formData.append('file', action.data.file[0].url);
        } else if ((action.data.file) && (action.data.file[0].url === null)) {
            formData.append('file', '');
        } else if ((action.data.file) && (action.data.file[0].originFileObj)) {
            formData.append('file', action.data.file[0].originFileObj);
        }

        if (action.data.colors) {
            const output = [];
            action.data.colors.forEach((item) => {
                // eslint-disable-next-line no-unused-vars
                const existing = output.filter((v, i) => v.name === item.colors.name);
                if (existing.length > 0) {
                    const existingIndex = output.indexOf(existing[0]);
                    output[existingIndex].rgb_code = output[existingIndex].rgb_code.concat(item.colors.rgb_code);
                } else {
                    output.push(item.colors);
                }
            });

            formData.append('colors', JSON.stringify(output));
        }
        const result = yield DesignLibraryApi.addNewDesignVariantApi(formData);

        if (result.status === 202) {
            const stateResult = yield DesignLibraryApi.toggleDesignLibraryItemStateApi({
                library_id: action.data.library_id,
                design_variant_id: action.data.design_variant_id,
                state: action.data.state,
            });

            if (stateResult.status !== 202) {
                setNotification('error', locale, 'global.error', 'list.notification.toggleState.error');
            }

            yield put(DesignLibraryActions.addDesignVariantRes(true));
            yield put(DesignLibraryActions.addDesignVariantRes(false));

            yield put(DesignLibraryActions.setEditableDesignVariant(null));
            yield call(loadLibrarySaga, { libraryID: action.data.library_id });

            setNotification('success', locale, 'global.success', finalDescription);
        } else {
            const error = yield result.json();

            setNotification('error', locale, 'global.error', error.message, { noTranslateMsg: true });
            yield put(DesignLibraryActions.addDesignVariantErr(error.message));
        }
    } catch (e) {
        setNotification('error', locale, 'global.error');
        yield put(DesignLibraryActions.addDesignVariantErr());
        console.log(e);
    }
}

export function* addDesignVariantWatcher() {
    yield takeLatest(DesignLibraryTypes.ADD_DESIGN_VARIANT, addDesignVariantSaga);
}
// A D D   N E W   D E S I G N   V A R I A N T

// L O A D   E D I T A B L E   D E S I G N   V A R I A N T
function* loadEdiTableDesignVariantSaga(action) {
    try {
        const params = {
            filter: JSON.stringify({
                _id: action.id,
            }),
        };

        const result = yield DesignLibraryApi.getLibraryApi(params);

        if (result.data.hits.length === 0) {
            notification.error({
                message: 'Error',
                description: 'Print version don\'t exist',
            });

            yield put(push('/404'));
            yield put(DesignLibraryActions.addDesignVariantErr());
        } else {
            const [variant] = result.data.hits;
            yield put(DesignLibraryActions.setSelectedDesign(variant));
            yield put(DesignLibraryActions.loadEdiTableDesignVariantRes(variant));
        }
    } catch (e) {
        yield put(DesignLibraryActions.addDesignVariantErr());
        console.log(e);
    }
}

export function* loadEdiTableDesignVariantWatcher() {
    yield takeLatest(DesignLibraryTypes.LOAD_EDITTABLE_DESIGN_VARIANT, loadEdiTableDesignVariantSaga);
}
// L O A D   E D I T A B L E   D E S I G N   V A R I A N T

// E D I T   D E S I G N   V A R I A N T
function* editDesignVariantSaga(action) {
    const layout = yield select(getLayout);
    const { locale } = layout;
    const finalDescription = 'designLibrary.notification.PrintVersionEditedSuccess';
    try {
        if (action.data) {
            const formData = new FormData();
            const dataKeys = Object.keys(action.data);

            dataKeys.forEach((key) => {
                if (key !== 'colors' && key !== 'file' && key !== 'qualities') {
                    formData.append(key, action.data[key]);
                }
            });

            if (action.data.qualities) {
                formData.append('qualities', JSON.stringify(action.data.qualities));
            }

            if (action.data.file && action.data.file[0] === null) {
                formData.append('file', '');
            } else if ((action.data.file) && (action.data.file[0].url)) {
                formData.append('file', action.data.file[0].url);
            } else if ((action.data.file) && (action.data.file[0].url === null)) {
                formData.append('file', '');
            } else if ((action.data.file) && (action.data.file[0].originFileObj)) {
                formData.append('file', action.data.file[0].originFileObj);
                yield put(DesignLibraryActions.setEditableImageFileToDesignVariant(action.data.design_variant_id));
            }

            if (action.data.colors) {
                const output = [];

                action.data.colors.forEach((item) => {
                    const existing = output.filter(v => v.name === item.colors.name);
                    if (existing.length > 0) {
                        const existingIndex = output.indexOf(existing[0]);
                        output[existingIndex].rgb_code = output[existingIndex].rgb_code.concat(item.colors.rgb_code);
                    } else {
                        output.push(item.colors);
                    }
                });

                formData.append('colors', JSON.stringify(output));
            }

            const result = yield DesignLibraryApi.editDesignVariantApi(formData);

            if (result.status === 202) {
                const stateResult = yield DesignLibraryApi.toggleDesignLibraryItemStateApi({
                    library_id: action.data.library_id,
                    design_variant_id: action.data.design_variant_id,
                    state: action.data.state,
                });

                if (stateResult.status !== 202) {
                    setNotification('error', locale, 'global.error', 'list.notification.toggleState.error');
                }
                yield put(DesignLibraryActions.editDesignVariantRes());
                yield put(DesignLibraryActions.editDesignVariantRes(false, true));
                yield delay(500);
                yield put(DesignLibraryActions.editDesignVariantRes(false, false));
                yield call(loadLibrarySaga, { libraryID: action.data.library_id });

                setNotification('success', locale, 'global.success', finalDescription);
            } else {
                const error = yield result.json();

                setNotification('error', locale, 'global.error', error.message, { noTranslateMsg: true });
                yield put(DesignLibraryActions.editDesignVariantErr(error.message));
            }
        }
    } catch (e) {
        yield put(DesignLibraryActions.editDesignVariantErr());
        console.log(e);
    }
}

export function* editDesignVariantWatcher() {
    yield takeLatest(DesignLibraryTypes.EDIT_DESIGN_VARIANT, editDesignVariantSaga);
}
// E D I T   D E S I G N   V A R I A N T

// U P D A T E   D E S I G N   L I B R A R Y   T R A N S L A T E
function* updateDesignLibraryTranslate(action) {
    const layout = yield select(getLayout);
    const { locale } = layout;

    let finalDescription = 'designLibrary.notification.newDesignLibraryAddedSuccess';
    let type = 'add';

    const data = {
        library_id: action.data.library_id,
        reseller_id: action.data.reseller_id,
    };

    if (action.type === DesignLibraryTypes.EDIT_DESIGN_LIBRARY) {
        type = 'edit';
        finalDescription = 'designLibrary.notification.DesignLibraryEditedSuccess';
    }

    for (let i = 0; i < locales.length; i += 1) {
        const lc = locales[i];

        if (lc !== 'en' || (lc === 'en' && action.type === DesignLibraryTypes.EDIT_DESIGN_LIBRARY)) {
            try {
                const dataTrans = {
                    ...data,
                    ...action.data[lc],
                };

                const resultTrans = yield DesignLibraryApi.updateDesignLibraryTranslateApi(dataTrans);

                if (resultTrans.status !== 202) {
                    const error = yield resultTrans.json();
                    setNotification('error', locale, 'global.error', error.message, { noTranslateMsg: true });
                }
            } catch (e) {
                setNotification('error', locale, 'global.error', 'property.notification.translateError', { language: getTranslated(`locale.${lc}`, locale) });
                console.log(e);
            }
        }
    }

    yield put(DesignLibraryActions.updateTranslateDesignLibrary(true));

    if (action.type === DesignLibraryTypes.EDIT_DESIGN_LIBRARY) {
        yield put(push('/design-library/list/'));
    }

    yield delay(500);
    yield put(DesignLibraryActions.updateTranslateDesignLibraryRes(type, false, false));

    setNotification('success', locale, 'global.success', finalDescription);
}
// U P D A T E   D E S I G N   L I B R A R Y   T R A N S L A T E

// L O A D   E D I T A B L E   D E S I G N   L I B R A R Y
function* loadEdiTableDesignLibrarySaga(action) {
    try {
        const paramTemplate = {
            filter: JSON.stringify({
                _id: action.id,
            }),
        };

        const resultTemplate = yield DesignLibraryApi.getLibraryApi(paramTemplate);

        if (resultTemplate.data.hits.length === 0) {
            notification.error({
                message: 'Error',
                description: 'Design don\'t exist',
            });

            yield put(push('/404'));
            yield put(DesignLibraryActions.loadEdiTableDesignLibraryErr());
        } else {
            const template = resultTemplate.data.hits[0];
            yield put(DesignLibraryActions.loadEdiTableDesignLibraryRes(template));
        }
    } catch (e) {
        yield put(DesignLibraryActions.loadEdiTableDesignLibraryErr());
        console.log(e);
    }
}

export function* loadEdiTableDesignLibraryWatcher() {
    yield takeLatest(DesignLibraryTypes.LOAD_EDITTABLE_DESIGN_LIBRARY, loadEdiTableDesignLibrarySaga);
}
// L O A D   E D I T A B L E   D E S I G N   L I B R A R Y

// A D D   N E W   D E S I G N   L I B R A R Y
function* addDesignLibrarySaga(action) {
    const layout = yield select(getLayout);
    const { locale } = layout;

    try {
        const data = {
            library_id: action.data.library_id,
            reseller_id: action.data.reseller_id,
            ...action.data.en,
            unique_name: action.data.en.name,
            comment: action.data.comment,
            theme_ids: action.data.theme_ids,
            sub_theme_ids: action.data.sub_theme_ids,
            category_ids: action.data.category_ids,
            exclusivities: action.data.exclusivities,
            tags: action.data.tags,
            prefix: action.data.prefix,
        };

        const result = yield DesignLibraryApi.addNewDesignLibraryApi(data);

        if (result.status === 202) {
            yield put(DesignLibraryActions.addDesignLibraryRes(true));
            yield call(updateDesignLibraryTranslate, action);
            yield delay(1000);
            yield put(DesignLibraryActions.addDesignLibraryRes(false));
            yield put(DesignLibraryActions.goToStepOfDesignForm(1, action.data.library_id));
            yield put(DesignLibraryActions.setSelectedDesign({
                _id: action.data.library_id,
                name: {
                    original_name: action.data.en.name,
                    translate: [
                        {
                            language: 'be_en',
                            value: action.data.en.name,
                        },
                        {
                            language: 'be_fr',
                            value: action.data.fr.name,
                        },
                        {
                            language: 'be_nl',
                            value: action.data.nl.name,
                        },
                    ],
                },
            }));
            yield call(loadEdiTableDesignVariantSaga, { id: action.data.library_id });
        } else {
            const error = yield result.json();

            setNotification('error', locale, 'global.error', error.message, { noTranslateMsg: true });
            yield put(DesignLibraryActions.addDesignLibraryErr(error.message));
        }
    } catch (e) {
        setNotification('error', locale, 'global.error');
        yield put(DesignLibraryActions.addDesignLibraryErr());
        console.log(e);
    }
}

export function* addDesignLibraryWatcher() {
    yield takeLatest(DesignLibraryTypes.ADD_DESIGN_LIBRARY, addDesignLibrarySaga);
}
// A D D   N E W   D E S I G N   L I B R A R Y

// E D I T   D E S I G N   L I B R A R Y
function* editDesignLibrarySaga(action) {
    const layout = yield select(getLayout);
    const { locale } = layout;

    try {
        const result = yield DesignLibraryApi.editDesignLibraryApi(action.data);

        if (result.status === 202) {
            yield call(updateDesignLibraryTranslate, action);
            yield put(DesignLibraryActions.editDesignLibraryRes());

            yield put(DesignLibraryActions.editDesignLibraryRes(false, true));
            yield delay(500);
            yield put(DesignLibraryActions.editDesignLibraryRes(false, false));
        } else {
            const error = yield result.json();

            setNotification('error', locale, 'global.error', error.message, { noTranslateMsg: true });
            yield put(DesignLibraryActions.editDesignLibraryErr(error.message));
        }
    } catch (e) {
        yield put(DesignLibraryActions.editDesignLibraryErr());
        console.log(e);
    }
}

export function* editDesignLibraryWatcher() {
    yield takeLatest(DesignLibraryTypes.EDIT_DESIGN_LIBRARY, editDesignLibrarySaga);
}
// E D I T   D E S I G N   L I B R A R Y

// D E L E T E   D E S I G N   L I B R A R Y
function* deleteDesignLibrarySaga(action) {
    const { locale } = yield select(getLayout);

    try {
        const result = yield DesignLibraryApi.deleteDesignLibraryApi(action.data);

        if (result.status === 202) {
            yield put(DesignLibraryActions.deleteDesignLibraryRes(true));
            yield delay(1000);
            yield put(DesignLibraryActions.deleteDesignLibraryRes(false));
            setNotification('success', locale, 'global.success', 'designLibrary.notification.deleteDesignLibrarySuccess');
            yield put(push('/design-library/list/'));
        } else {
            yield put(DesignLibraryActions.deleteDesignLibraryErr());
            setNotification('error', locale, 'global.error', 'designLibrary.notification.deleteDesignLibraryError');
        }
    } catch (e) {
        yield put(DesignLibraryActions.deleteDesignLibraryErr());
        setNotification('error', locale, 'global.error', 'designLibrary.notification.deleteDesignLibraryError');
        console.log(e);
    }
}

export function* deleteDesignLibraryWatcher() {
    yield takeLatest(DesignLibraryTypes.DELETE_DESIGN_LIBRARY, deleteDesignLibrarySaga);
}
// D E L E T E   D E S I G N   L I B R A R Y

// D E L E T E   D E S I G N   V A R I A N T
function* deleteDesignVariantSaga(action) {
    const { locale } = yield select(getLayout);
    const designLibrary = yield select(getDesignLibrary);
    const { data } = designLibrary.listDesignVariant;
    const { listing } = designLibrary.view;
    const pathDesignVariant = window.location.pathname.split('/')[2];

    try {
        const result = yield DesignLibraryApi.deleteDesignVariantApi(action.data);
        if (result.status === 202) {
            yield put(DesignLibraryActions.deleteDesignVariantRes(true));
            yield delay(1000);
            yield put(DesignLibraryActions.deleteDesignVariantRes(false));
            setNotification('success', locale, 'global.success', 'designLibrary.notification.deletePrintVersionSuccess');

            if (pathDesignVariant === 'view') {
                if (listing.total > 0) {
                    const remainingPrintVersion = listing.hits[0].design_variants.find(pv => pv.design_variant_id !== action.data.design_variant_id);
                    remainingPrintVersion
                        ? yield put(push(`/design-library/view/${listing.hits[0]._id}/${remainingPrintVersion.design_variant_id}`))
                        : yield put(push(`/design-library/view/${listing.hits[0]._id}/no-variants`));
                }
            }

            if (pathDesignVariant === 'edit') {
                if (data.hits.length > 0) {
                    const remainingDesignVariant = data.hits?.find(pv => pv.design_variant_id !== action.data.design_variant_id);
                    remainingDesignVariant
                        ? yield put(push(`/design-variant/edit/${remainingDesignVariant.design_variant_id}/${remainingDesignVariant.library_id}`))
                        : yield put(push('/design-library/list/'));
                }
            }

            if (pathDesignVariant === 'add') {
                if (data.hits.length > 0) {
                    const remainingDesignVariant = data.hits.find(pv => pv.design_variant_id !== action.data.design_variant_id);
                    remainingDesignVariant
                        ? yield put(push(`/design-library/add/${remainingDesignVariant.design_variant_id}/${remainingDesignVariant.library_id}`))
                        : yield put(push('/design-library/list/'));
                }
            }
        } else {
            yield put(DesignLibraryActions.deleteDesignVariantErr());
            setNotification('error', locale, 'global.error', 'designLibrary.notification.deletePrintVersionError');
        }
    } catch (e) {
        yield put(DesignLibraryActions.deleteDesignVariantErr());
        setNotification('error', locale, 'global.error', 'designLibrary.notification.deletePrintVersionError');
        console.log(e);
    }
}

export function* deleteDesignVariantWatcher() {
    yield takeLatest(DesignLibraryTypes.DELETE_DESIGN_VARIANT, deleteDesignVariantSaga);
}
// D E L E T E   D E S I G N   V A R I A N T

// L O A D   F I N I S H I N G   P R O P E R T Y   V A L U E S
function* loadFinishingPropertyValuesSaga() {
    const { locale } = yield select(getLayout);

    try {
        const result = yield getPropertyValuesApi({
            offset: 0,
            limit: 999,
            filter: JSON.stringify({
                property: process.env.REACT_APP_FINISHING_MF_PROPERTY_ID,
            }),
        });

        if (result.status === 200) {
            yield put(DesignLibraryActions.loadFinishingPropertyValuesRes(result.data?.hits));
        } else {
            yield put(DesignLibraryActions.loadFinishingPropertyValuesErr());
            setNotification('error', locale, 'global.error', 'designLibrary.notification.finishingPropertyValues.error');
        }
    } catch (e) {
        yield put(DesignLibraryActions.deleteDesignLibraryErr());
        setNotification('error', locale, 'global.error', 'designLibrary.notification.finishingPropertyValues.error');
        console.log(e);
    }
}

export function* loadFinishingPropertyValuesWatcher() {
    yield takeLatest(DesignLibraryTypes.LOAD_FINISHING_PROPERTY_VALUES, loadFinishingPropertyValuesSaga);
}
// L O A D   F I N I S H I N G   P R O P E R T Y   V A L U E S
